import axios from "axios";

import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useAxiosHook } from "./useAxiosHook";

/**
 * Loan notes — `/notes` router (loan_and_auth_endpoints.md §11).
 *
 * Rich-text underwriting & processing notes, stored SEPARATELY from loan
 * `comments` and from the field-based `/underwriting/underwritingNotes/{dealId}`
 * (one doc per loan with `underwriting` + `processing` sub-objects sharing the
 * same schema). The save body is the BlockNote payload (`json` + `markdown` +
 * `html`); pasted base64 images are relocated into the loan's blob container
 * server-side and referenced by URL.
 *
 *   GET  /notes/{dealId}               -> { _id, dealId, underwriting, processing }
 *   POST /notes/{dealId}/underwriting  -> saved underwriting note
 *   POST /notes/{dealId}/processing    -> saved processing note
 */
const ROUTE_PREFIX = "notes";

export const useNotesHook = () => {
  const {
    axiosGetIsLoading,
    axiosPostIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  // GET /notes/{dealId} — both note types ({ underwriting, processing } may be null).
  const getNotes = async ({ dealId, onSuccessFn, onCompleteFn, onFailFn }) => {
    validateRequiredFields({ dealId });
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Loan Notes",
      path: `${ROUTE_PREFIX}/${dealId}`,
      cancelSource: source,
      hideFailMsg: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /notes/{dealId}/underwriting — create/update the underwriting note.
  const saveUnderwritingNote = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
    onSuccessMsg = "Underwriting note saved",
  }) => {
    validateRequiredFields({ dealId, data });

    await axiosPostRequest({
      name: "Save Underwriting Note",
      path: `${ROUTE_PREFIX}/${dealId}/underwriting`,
      payload: data,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  // POST /notes/{dealId}/processing — create/update the processing note.
  const saveProcessingNote = async ({
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
    onSuccessMsg = "Processing note saved",
  }) => {
    validateRequiredFields({ dealId, data });

    await axiosPostRequest({
      name: "Save Processing Note",
      path: `${ROUTE_PREFIX}/${dealId}/processing`,
      payload: data,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    loading,
    error,
    getNotes,
    saveUnderwritingNote,
    saveProcessingNote,
  };
};

export default useNotesHook;
